import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const Governance = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("proposals");

  const governanceStats = [
    { label: "Total Voters", value: "24.8K", change: "+18%" },
    { label: "Proposals", value: "142", change: "+12%" },
    { label: "Voting Power", value: "42M XEC", change: "+25%" },
    { label: "Participation Rate", value: "68%", change: "+8%" }
  ];

  const activeProposals = [
    {
      id: "XGP-42",
      title: "Ecosystem Fund Allocation",
      description: "Proposal to allocate 5M XEC for ecosystem development grants",
      votesFor: "12.4M",
      votesAgainst: "2.1M",
      endDate: "3 days left",
      status: "Active"
    },
    {
      id: "XGP-41",
      title: "Transaction Fee Reduction",
      description: "Reduce base transaction fees by 20% to increase adoption",
      votesFor: "8.7M",
      votesAgainst: "4.2M",
      endDate: "1 day left",
      status: "Active"
    },
    {
      id: "XGP-40",
      title: "Bridge Integration with Solana",
      description: "Add Solana network support to cross-chain bridge",
      votesFor: "15.2M",
      votesAgainst: "1.8M",
      endDate: "Completed",
      status: "Passed"
    }
  ];

  const votingTiers = [
    {
      tier: "Community Member",
      minStake: "100 XEC",
      votingPower: "1x",
      features: ["Basic voting", "Proposal discussion", "Community polls"]
    },
    {
      tier: "Active Contributor",
      minStake: "1,000 XEC",
      votingPower: "2.5x",
      features: ["Enhanced voting", "Proposal creation", "Governance forums", "Early access"]
    },
    {
      tier: "Eco-Delegate",
      minStake: "10,000 XEC",
      votingPower: "5x",
      features: ["Max voting power", "Delegate voting", "Treasury access", "Council nomination"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            🏛️
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">Governance</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Participate in network decisions with our democratic governance system. 
            Your voice matters in shaping the future of X EcoChain.
          </p>
        </div>

        {/* Governance Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {governanceStats.map((stat, index) => (
            <div key={index} className="glass-card p-6 rounded-2xl text-center border border-eco-green border-opacity-20">
              <div className="text-2xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-gray-400 text-sm mb-1">{stat.label}</div>
              <div className="text-eco-green text-sm font-bold">{stat.change}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-700 rounded-2xl p-2 mb-8">
          <button
            className={`flex-1 py-3 text-center font-bold rounded-xl transition-all duration-300 ${
              activeTab === "proposals"
                ? "bg-gradient-to-r from-eco-green to-eco-blue text-white shadow-lg"
                : "text-gray-400 hover:text-white bg-gray-600"
            }`}
            onClick={() => setActiveTab("proposals")}
          >
            Active Proposals
          </button>
          <button
            className={`flex-1 py-3 text-center font-bold rounded-xl transition-all duration-300 ${
              activeTab === "create"
                ? "bg-gradient-to-r from-eco-green to-eco-blue text-white shadow-lg"
                : "text-gray-400 hover:text-white bg-gray-600"
            }`}
            onClick={() => setActiveTab("create")}
          >
            Create Proposal
          </button>
          <button
            className={`flex-1 py-3 text-center font-bold rounded-xl transition-all duration-300 ${
              activeTab === "delegates"
                ? "bg-gradient-to-r from-eco-green to-eco-blue text-white shadow-lg"
                : "text-gray-400 hover:text-white bg-gray-600"
            }`}
            onClick={() => setActiveTab("delegates")}
          >
            Delegates
          </button>
        </div>

        {/* Active Proposals */}
        {activeTab === "proposals" && (
          <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
            <h2 className="text-3xl font-black text-white mb-8">Active Proposals</h2>
            <div className="space-y-6">
              {activeProposals.map((proposal, index) => (
                <div key={index} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-eco-green font-bold mr-3">{proposal.id}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          proposal.status === "Active" ? "bg-green-900 text-green-400" : "bg-blue-900 text-blue-400"
                        }`}>
                          {proposal.status}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{proposal.title}</h3>
                      <p className="text-gray-400">{proposal.description}</p>
                    </div>
                    <div className="mt-4 md:mt-0 md:ml-6 text-right">
                      <div className="text-sm text-gray-400 mb-2">{proposal.endDate}</div>
                      <div className="text-eco-green font-bold">For: {proposal.votesFor} XEC</div>
                      <div className="text-red-400 font-bold">Against: {proposal.votesAgainst} XEC</div>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <button className="flex-1 py-2 bg-green-900 text-green-400 rounded-xl font-bold hover:bg-green-800 transition-colors">
                      Vote For
                    </button>
                    <button className="flex-1 py-2 bg-red-900 text-red-400 rounded-xl font-bold hover:bg-red-800 transition-colors">
                      Vote Against
                    </button>
                    <button className="px-6 py-2 bg-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-600 transition-colors">
                      Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Voting Tiers */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Governance Tiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {votingTiers.map((tier, index) => (
              <div key={index} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{tier.tier}</h3>
                <div className="text-3xl font-black text-eco-green mb-4">{tier.votingPower} Voting Power</div>
                <p className="text-gray-400 mb-4">Min Stake: {tier.minStake}</p>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-eco-blue">
                      <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3 bg-eco-green text-eco-black font-bold rounded-xl hover:bg-eco-blue transition-colors">
                  Join Tier
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Governance Process */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">How Governance Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-eco-green rounded-2xl flex items-center justify-center text-eco-black text-xl mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Stake Tokens</h3>
              <p className="text-gray-300 text-sm">Stake XEC tokens to gain voting power and governance rights</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-eco-blue rounded-2xl flex items-center justify-center text-eco-black text-xl mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Create/Discuss</h3>
              <p className="text-gray-300 text-sm">Submit proposals or participate in community discussions</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-eco-purple rounded-2xl flex items-center justify-center text-eco-black text-xl mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Vote</h3>
              <p className="text-gray-300 text-sm">Cast your vote on active proposals using your voting power</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-eco-green rounded-2xl flex items-center justify-center text-eco-black text-xl mx-auto mb-4">
                4
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Implement</h3>
              <p className="text-gray-300 text-sm">Successful proposals are automatically executed on-chain</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Governance;